import styled, { css } from "styled-components";
import { Link } from "react-router-dom";
import {
  glassCardStyles,
  Page,
  PrimaryButton,
  theme,
  typography,
} from "../Homepage/styles";

/** Override global a:hover underline (shards-dashboards) on card-style links. */
const cardLinkNoUnderline = css`
  &,
  &:hover,
  &:focus,
  &:active,
  &:visited {
    text-decoration: none;
    color: inherit;
  }

  &:hover *,
  &:focus *,
  &:active *,
  &:visited * {
    text-decoration: none;
  }
`;

export const AnalystProfilePage = styled(Page)`
  background: hsl(var(--hp-background));
  background-image: none;
`;

export const AnalystNotFoundSection = styled.section`
  padding: 128px 16px 80px;
  text-align: center;
`;

export const AnalystNotFoundTitle = styled.h1`
  margin: 0 0 16px;
  color: ${theme.foreground};
  font-size: 1.875rem;
  font-weight: ${typography.weights.bold};
`;

export const AnalystNotFoundText = styled.p`
  margin: 0 0 24px;
  color: ${theme.textMuted};
`;

export const AnalystNotFoundButton = styled(PrimaryButton)`
  min-height: 44px;
  padding: 0 24px;
  text-decoration: none;

  &:hover,
  &:focus,
  &:active,
  &:visited {
    text-decoration: none;
  }
`;

export const AnalystHeroSection = styled.section`
  padding: 96px 16px 48px;

  @media (min-width: 768px) {
    padding: 112px 16px 64px;
  }
`;

export const AnalystHeroInner = styled.div`
  max-width: 80rem;
  margin: 0 auto;
`;

export const AnalystBackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 24px;
  color: ${theme.textMuted};
  font-size: 0.875rem;
  text-decoration: none;
  transition: color 0.2s ease;

  &:hover {
    color: ${theme.primary};
    text-decoration: none;
  }

  svg {
    flex-shrink: 0;
    width: 16px;
    height: 16px;
  }
`;

export const AnalystHeroShell = styled.div`
  ${glassCardStyles}
  border-radius: 1rem;
  border-color: hsl(var(--hp-border) / 0.5);
  padding: 24px;

  @media (min-width: 768px) {
    padding: 32px;
  }
`;

export const AnalystHeroGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 32px;
  align-items: start;

  @media (min-width: 1024px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 40px;
  }
`;

export const AnalystHeroBioCol = styled.div``;

export const AnalystHeroVideoCol = styled.div``;

export const AnalystIdentityRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  text-align: center;

  @media (min-width: 640px) {
    flex-direction: row;
    align-items: flex-start;
    text-align: left;
  }
`;

export const AnalystAvatar = styled.img`
  flex-shrink: 0;
  width: 112px;
  height: 112px;
  border-radius: 9999px;
  object-fit: cover;
  box-shadow: 0 0 0 2px hsl(var(--hp-primary) / 0.4);
  background: hsl(var(--hp-secondary));

  @media (min-width: 768px) {
    width: 144px;
    height: 144px;
  }
`;

export const AnalystIdentityMeta = styled.div`
  flex: 1;
  min-width: 0;
`;

export const AnalystNameRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 8px;

  @media (min-width: 640px) {
    justify-content: flex-start;
  }
`;

export const AnalystName = styled.h1`
  margin: 0;
  color: ${theme.foreground};
  font-size: 1.875rem;
  font-weight: ${typography.weights.bold};
  line-height: 1.2;

  @media (min-width: 768px) {
    font-size: 2.25rem;
  }
`;

export const AnalystVerifiedIcon = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  color: ${theme.primary};

  svg {
    display: block;
    width: 24px;
    height: 24px;
  }
`;

export const AnalystRoleBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border: 1px solid hsl(var(--hp-primary) / 0.2);
  border-radius: 9999px;
  background: hsl(var(--hp-primary) / 0.1);
  color: ${theme.primary};
  font-size: 0.625rem;
  font-weight: ${typography.weights.semibold};
  letter-spacing: 0.12em;
  text-transform: uppercase;
`;

export const AnalystBioBlock = styled.div`
  margin-top: 24px;
  color: ${theme.textMuted};
  font-size: 0.9375rem;
  line-height: 1.65;
  white-space: pre-line;

  button {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-top: 12px;
    padding: 0;
    border: none;
    background: transparent;
    color: ${theme.primary};
    font-family: inherit;
    font-size: 0.875rem;
    font-weight: ${typography.weights.semibold};
    cursor: pointer;

    svg {
      width: 16px;
      height: 16px;
    }
  }
`;

export const AnalystVideoFrame = styled.div`
  position: relative;
  overflow: hidden;
  height: 270px;
  border: 1px solid hsl(var(--hp-border) / 0.6);
  border-radius: 0.75rem;
  background: hsl(var(--hp-secondary));
`;

export const AnalystSplitSection = styled.section`
  padding: 48px 16px;
  border-top: 1px solid hsl(var(--hp-border) / 0.5);
  background: hsl(var(--hp-card) / 0.2);
`;

export const AnalystSplitGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 32px;
  max-width: 80rem;
  margin: 0 auto;

  @media (min-width: 1024px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

export const AnalystPanel = styled.div``;

export const AnalystPanelHead = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
`;

export const AnalystPanelTitle = styled.h2`
  margin: 0;
  color: ${theme.foreground};
  font-size: 1.25rem;
  font-weight: ${typography.weights.bold};

  @media (min-width: 768px) {
    font-size: 1.5rem;
  }
`;

export const AnalystPanelDesc = styled.p`
  margin: 4px 0 0;
  color: ${theme.textMuted};
  font-size: 0.875rem;
  line-height: 1.5;
`;

export const AnalystSeeAllLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0;
  border: none;
  background: transparent;
  color: ${theme.primary};
  font-family: inherit;
  font-size: 0.875rem;
  font-weight: ${typography.weights.semibold};
  cursor: pointer;
  transition: gap 0.3s ease;

  svg {
    width: 16px;
    height: 16px;
  }

  &:hover {
    gap: 10px;
  }
`;

export const AnalystPreviewList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const AnalystPreviewLink = styled(Link)`
  display: block;
  padding: 16px;
  border: 1px solid hsl(var(--hp-border) / 0.5);
  border-radius: 0.75rem;
  background: hsl(var(--hp-card) / 0.6);
  color: inherit;
  text-decoration: none;
  transition:
    border-color 0.2s ease,
    background-color 0.2s ease;

  ${cardLinkNoUnderline}

  &:hover {
    border-color: hsl(var(--hp-primary) / 0.4);
    background: hsl(var(--hp-card) / 0.9);
  }
`;

export const AnalystPreviewTitle = styled.p`
  margin: 0;
  color: ${theme.foreground};
  font-size: 0.9375rem;
  font-weight: ${typography.weights.medium};
  line-height: 1.45;
`;

export const AnalystPreviewDate = styled.p`
  margin: 6px 0 0;
  color: ${theme.textMuted};
  font-size: 0.75rem;
  line-height: 1.4;
`;

export const AnalystAlertsGateCard = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  border-radius: 0.75rem;
  border: 1px solid hsl(var(--hp-primary) / 0.25);
  background: hsl(var(--hp-primary) / 0.05);
`;

export const AnalystAlertsGateIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 0.5rem;
  border: 1px solid hsl(var(--hp-primary) / 0.2);
  background: hsl(var(--hp-primary) / 0.1);
  color: ${theme.primary};

  svg {
    display: block;
    flex-shrink: 0;
  }
`;

export const AnalystAlertsGateBody = styled.div`
  flex: 1;
  min-width: 0;
`;

export const AnalystAlertsGateTitle = styled.p`
  margin: 0;
  color: ${theme.foreground};
  font-size: 0.875rem;
  font-weight: ${typography.weights.bold};
  line-height: 1.3;
`;

export const AnalystAlertsGateText = styled.p`
  margin: 2px 0 0;
  color: ${theme.textMuted};
  font-size: 0.75rem;
  line-height: 1.5;
`;

export const AnalystAlertsGateButton = styled(PrimaryButton)`
  min-height: 32px;
  margin-top: 12px;
  padding: 0 14px;
  border-radius: var(--hp-radius, 0.75rem);
  font-size: 0.8125rem;
  text-decoration: none;

  &:hover,
  &:focus,
  &:active,
  &:visited {
    text-decoration: none;
  }
`;

export const AnalystGuestFooter = styled.section`
  padding: 0 16px 40px;
`;

export const AnalystGuestFooterInner = styled.p`
  max-width: 80rem;
  margin: 0 auto;
  color: ${theme.textMuted};
  font-size: 0.875rem;
  text-align: center;

  a {
    color: ${theme.primary};
    font-weight: ${typography.weights.semibold};
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

export const AnalystEmptyState = styled.div`
  padding: 24px 16px;
  border: 1px dashed hsl(var(--hp-border) / 0.6);
  border-radius: 0.75rem;
  background: hsl(var(--hp-card) / 0.4);
  text-align: center;

  strong {
    display: block;
    margin-bottom: 6px;
    color: ${theme.foreground};
    font-size: 0.875rem;
  }

  p {
    margin: 0;
    color: ${theme.textMuted};
    font-size: 0.8125rem;
    line-height: 1.5;
  }
`;

export const AnalystEducationSection = styled.section`
  padding: 48px 16px;
  border-top: 1px solid hsl(var(--hp-border) / 0.5);
`;

export const AnalystEducationInner = styled.div`
  max-width: 80rem;
  margin: 0 auto;
`;

export const AnalystEducationGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;

  @media (min-width: 768px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`;

export const AnalystEducationCard = styled(Link)`
  display: block;
  padding: 24px;
  border: 1px solid hsl(var(--hp-border) / 0.5);
  border-radius: 1rem;
  ${glassCardStyles}
  color: inherit;
  text-decoration: none;
  transition:
    transform 0.3s ease,
    border-color 0.3s ease;

  ${cardLinkNoUnderline}

  &:hover {
    transform: translateY(-4px);
    border-color: hsl(var(--hp-primary) / 0.4);
  }
`;

export const AnalystEducationIconWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  margin-bottom: 16px;
  border: 1px solid hsl(var(--hp-primary) / 0.2);
  border-radius: 0.75rem;
  background: hsl(var(--hp-primary) / 0.1);
  color: ${theme.primary};
`;

export const AnalystEducationTitle = styled.p`
  margin: 0;
  color: ${theme.foreground};
  font-size: 0.9375rem;
  font-weight: ${typography.weights.semibold};
  line-height: 1.45;
`;

export const AnalystEducationDate = styled.p`
  margin: 8px 0 0;
  color: ${theme.textMuted};
  font-size: 0.75rem;
`;

export const AnalystEducationRead = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 16px;
  color: ${theme.primary};
  font-size: 0.875rem;
  font-weight: ${typography.weights.semibold};

  svg {
    width: 16px;
    height: 16px;
  }

  ${AnalystEducationCard}:hover & {
    gap: 10px;
  }
`;

export const AnalystSkeletonHero = styled.div`
  height: 360px;
  border-radius: 1rem;
  border: 1px solid hsl(var(--hp-border) / 0.5);
  background: hsl(var(--hp-muted) / 0.35);
  animation: analyst-profile-pulse 1.5s ease-in-out infinite;

  @keyframes analyst-profile-pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.55;
    }
  }
`;
